import { CrewRestDto } from '../../../api/scheduler/models/crew-rest-dto';
import { FixedWingEligibleCrewDto } from '../../../api/scheduler/models/fixed-wing-eligible-crew-dto';
import { FixedWingLineCrewDto } from '../../../api/scheduler/models/fixed-wing-line-crew-dto';
import { FixedWingLineDto } from '../../../api/scheduler/models/fixed-wing-line-dto';
import { FixedWingLineOptionDto } from '../../../api/scheduler/models/fixed-wing-line-option-dto';
import {
  FixedWingCrew,
  FixedWingEligibleCrew,
  FixedWingLine,
  FixedWingSavedLine,
} from '../models/fixed-wing.models';

/** Length of the rest window; matches the server's 10-hour rest policy. */
const REST_WINDOW_MINUTES = 10 * 60;

/** Minutes remaining in the 10-hour rest window, computed from the persisted start timestamp. */
function remainingRestMinutes(rest: CrewRestDto | undefined): number {
  if (!rest?.resting || !rest.startRestAt) {
    return 0;
  }
  const elapsedMinutes = (Date.now() - new Date(rest.startRestAt).getTime()) / 60000;
  return Math.max(0, Math.round(REST_WINDOW_MINUTES - elapsedMinutes));
}

/** Formats the remaining rest window as "Xh Ym left"; omitted once rest has elapsed. */
function restLabel(rest: CrewRestDto | undefined): string | undefined {
  const remainingMinutes = remainingRestMinutes(rest);
  if (remainingMinutes <= 0) {
    return undefined;
  }
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;
  return `${hours}h ${minutes}m left`;
}

function mapLineCrew(dto: FixedWingLineCrewDto): FixedWingCrew {
  return {
    crewId: dto.crewId,
    crewName: dto.crewName,
    rank: dto.rank,
    status: dto.status,
    restLabel: restLabel(dto.rest),
    resting: dto.rest?.resting ?? false,
  };
}

export function mapFixedWingLineDto(dto: FixedWingLineDto): FixedWingLine {
  return {
    id: dto.id,
    date: dto.date,
    name: dto.name,
    order: dto.order,
    stage: dto.stage,
    priorityOrder: dto.priorityOrder,
    createdAt: dto.createdAt ?? '',
    active: dto.active,
    locked: dto.locked,
    hasPrimaryRank: dto.hasPrimaryRank,
    status: dto.missionStatus ?? null,
    availableActions: dto.availableActions,
    crew: dto.crew.map(mapLineCrew),
  };
}

export function mapFixedWingLineOptionDto(dto: FixedWingLineOptionDto): FixedWingSavedLine {
  return {
    id: dto.id,
    date: dto.date,
    name: dto.name,
    order: dto.order,
    stage: dto.stage,
    active: dto.active,
    locked: dto.locked,
    isDefault: dto.isDefault,
  };
}

export function mapFixedWingEligibleCrewDto(dto: FixedWingEligibleCrewDto): FixedWingEligibleCrew {
  return {
    crewId: dto.crewId,
    crewName: dto.crewName,
    rank: dto.rank,
    assignedLineId: dto.assignedLineId ?? null,
    resting: dto.rest?.resting ?? false,
    restLabel: restLabel(dto.rest),
  };
}
